import { useState } from 'react'
import { Mode, ParsedObject, SpaceType } from '../../types'
import {
    Card,
    Typography,
    Grid,
    useTheme,
    Chip,
    Tooltip,
    Box,
    Checkbox,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    CircularProgress,
} from '@mui/material'
import { SPACE_TYPE_IMG_SRC, MODE_ICONS } from '../../constants'
import PilotChip from './PilotChip'
import OccupancyIndicator from './OccupancyIndicator'
import { Link } from 'react-router-dom'
import WarningIcon from '@mui/icons-material/Warning'
import { useTranslation } from 'react-i18next'
import { useRealtime } from '../../useRealtime'
import get from 'lodash/get'
import DeviceThermostatIcon from '@mui/icons-material/DeviceThermostat'

export interface ObjectCardProps {
    object: ParsedObject
    hasMoreData?: boolean
    showInletTemperature?: boolean
    isSelectionMode?: boolean
    isSelected?: boolean
    onToggleSelection?: (objectId: number) => void
}

const ObjectCard = ({
    object,
    hasMoreData,
    showInletTemperature = false,
    isSelectionMode = false,
    isSelected = false,
    onToggleSelection,
}: ObjectCardProps) => {
    const {
        objectId,
        spaceType,
        pilot,
        state,
        mode,
        temperature,
        setpointTemperature,
        constraints,
        occupied,
        isError,
        error,
    } = object
    const theme = useTheme()
    const { spacing } = theme
    const { t } = useTranslation('site', { keyPrefix: 'realtime' })
    const { errorCodeTranslations } = useRealtime()

    const [openModal, setOpenModal] = useState(false)
    const [openAirHandlerModal, setOpenAirHandlerModal] = useState(false)

    const isAirHandlerUnit = object?.consumptionUnitTechnologyType === 'AIR HANDLER UNIT'
    const isSelectableInSelectionMode = !isError && !isAirHandlerUnit

    const handleClick = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
        if (isSelectionMode) {
            e.preventDefault()
            if (isSelectableInSelectionMode) {
                onToggleSelection?.(objectId)
            }
            return
        }

        if (isError) {
            e.preventDefault()
            setOpenModal(true)
        } else if (isAirHandlerUnit) {
            e.preventDefault()
            setOpenAirHandlerModal(true)
        }
    }

    const handleClose = () => {
        setOpenModal(false)
    }

    const handleAirHandlerModalClose = () => {
        setOpenAirHandlerModal(false)
    }

    const errorMessage = get(errorCodeTranslations, [String(object.pluginId), String(error), 'Message'], '')
    const card = (
        <Card
            sx={{
                height: spacing(13),
                backgroundImage: spaceType ? `url(${SPACE_TYPE_IMG_SRC[spaceType]})` : undefined,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                boxShadow: state ? '0px 0px 4px 4px rgba(118,255,3,0.44)' : 'none',
                backgroundBlur: state ? 4 : 0,
                position: 'relative',
                cursor: isSelectionMode
                    ? isSelectableInSelectionMode
                        ? 'pointer'
                        : 'not-allowed'
                    : isAirHandlerUnit
                      ? 'not-allowed'
                      : 'pointer',
                opacity: isAirHandlerUnit && !isSelectionMode ? 0.7 : 1,
                outline: isSelectionMode && isSelected ? `2px solid ${theme.palette.primary.main}` : 'none',
                outlineOffset: isSelectionMode && isSelected ? 2 : 0,
                overflow: 'hidden',
            }}
        >
            {isSelectionMode && (
                <>
                    <Checkbox
                        checked={isSelected}
                        disabled={!isSelectableInSelectionMode}
                        sx={{
                            position: 'absolute',
                            top: 4,
                            left: 4,
                            zIndex: 2,
                            color: 'common.white',
                            '&.Mui-checked': {
                                color: 'common.white',
                            },
                            '&.Mui-disabled': {
                                color: 'rgba(255,255,255,0.5)',
                            },
                        }}
                    />
                    {isSelected && (
                        <Box
                            sx={{
                                position: 'absolute',
                                inset: 0,
                                backgroundColor: 'primary.main',
                                opacity: 0.18,
                                zIndex: 1,
                            }}
                        />
                    )}
                </>
            )}
            {hasMoreData && setpointTemperature == null && (
                <CircularProgress size={24} sx={{ position: 'absolute', top: 2, right: 2, color: 'white' }} />
            )}
            <Grid
                container
                direction="column"
                justifyContent="space-between"
                height="100%"
                p={1}
                sx={{
                    backgroundColor: state ? 'none' : 'rgba(0,0,0,0.6)',
                    position: 'relative',
                    zIndex: 1,
                }}
            >
                <Grid item container justifyContent="space-between">
                    {constraints ? (
                        <Grid item>
                            {isSelectionMode ? (
                                ''
                            ) : (
                                <Typography variant="caption" color="common.white" fontWeight="medium">
                                    {constraints?.min?.toFixed(0)}°C - {constraints?.max?.toFixed(0)}°C
                                </Typography>
                            )}
                        </Grid>
                    ) : (
                        <Grid item></Grid>
                    )}
                    <Grid item>
                        {(setpointTemperature != null || mode === Mode.FAN) && (
                            <Chip
                                icon={<img src={MODE_ICONS[mode]} width={16} />}
                                label={
                                    mode === Mode.FAN
                                        ? '-°C'
                                        : setpointTemperature
                                          ? `${setpointTemperature.toFixed(0)}°C`
                                          : '-°C'
                                }
                                sx={{ backgroundColor: 'grey.200', mr: 1, fontSize: 10, fontWeight: 'bold' }}
                                size="small"
                            />
                        )}
                        <PilotChip pilot={pilot} label={t(`objectCard.pilot.${pilot ? 'ON' : 'OFF'}`)} />
                    </Grid>
                </Grid>
                {showInletTemperature && (
                    <Grid item container justifyContent="flex-end">
                        <Tooltip title={t('objectCard.inletTemperature')} placement="top">
                            <Box
                                sx={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 0.25,
                                    px: 0.75,
                                    py: 0.25,
                                    color: 'common.white',
                                    backgroundColor: 'rgba(5, 12, 34, 0.72)',
                                    borderRadius: 1,
                                    backdropFilter: 'blur(2px)',
                                }}
                            >
                                <DeviceThermostatIcon sx={{ fontSize: 14, opacity: 0.85 }} />
                                <Typography
                                    component="span"
                                    variant="caption"
                                    sx={{ fontSize: 11, fontWeight: 600, lineHeight: 1.2 }}
                                >
                                    {temperature != null ? `${temperature.toFixed(1)}°C` : '-°C'}
                                </Typography>
                            </Box>
                        </Tooltip>
                    </Grid>
                )}
                <Grid item container justifyContent="space-between" alignItems="baseline">
                    <Typography
                        variant="subtitle1"
                        color="common.white"
                        fontWeight="bold"
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            fontSize: object?.name?.length > 20 ? 12 : 15,
                            lineHeight: 1.2,
                        }}
                    >
                        {object?.name?.length > 40
                            ? object?.name?.substring(0, 40)?.toUpperCase()
                            : object?.name?.toUpperCase()}
                        {isError && (
                            <Tooltip title={`${t('error')}: ${error} ${errorMessage}`} placement="top">
                                <WarningIcon color="warning" />
                            </Tooltip>
                        )}
                    </Typography>
                    {spaceType === SpaceType.HOTEL_ROOM && <OccupancyIndicator occupied={occupied} />}
                </Grid>
            </Grid>
        </Card>
    )

    return (
        <>
            <Link to={isError || isAirHandlerUnit ? '' : `${objectId}`} onClick={handleClick}>
                {card}
            </Link>

            {/* Modal for error details */}
            <Dialog open={openModal} onClose={handleClose}>
                <DialogTitle>{t('objectError')}</DialogTitle>
                <DialogContent dividers>
                    <Typography variant="body1">
                        <b> {t('errorCode')}</b>: {error}
                    </Typography>
                    <Typography variant="body2" mt={2}>
                        {errorMessage}
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose} color="primary">
                        {t('close')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Modal for Air Handler Unit notification */}
            <Dialog open={openAirHandlerModal} onClose={handleAirHandlerModalClose}>
                <DialogTitle>{t('notModifiable')}</DialogTitle>
                <DialogContent dividers>
                    <Typography variant="body1">{t('airHandlerUnitCannotBeModified')}</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleAirHandlerModalClose} color="primary">
                        {t('close')}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    )
}

export default ObjectCard
